// Đổi qua lại giữa phần trăm (lưu trong mẫu vé) và pixel (hiển thị trong trình soạn).
// Mọi khung trong mẫu vé lưu theo % ảnh mẫu: x/w và cỡ chữ theo chiều rộng, y/h theo chiều cao.

/** Khung chữ nhật theo phần trăm ảnh mẫu. */
export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Khung theo pixel đang hiển thị (đã nhân tỉ lệ thu nhỏ). */
export interface PxBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Khung QR trong mẫu: vuông, `size` là % chiều rộng ảnh. */
export interface QrPos {
  x: number;
  y: number;
  size: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;
const clampPct = (n: number) => Math.min(100, Math.max(0, n));

/** % -> px. `imgW/imgH` là kích thước gốc của ảnh, `scale` = bề rộng hiển thị / imgW. */
export function pctToPx(box: Box, imgW: number, imgH: number, scale: number): PxBox {
  return {
    left: (box.x / 100) * imgW * scale,
    top: (box.y / 100) * imgH * scale,
    width: (box.w / 100) * imgW * scale,
    height: (box.h / 100) * imgH * scale,
  };
}

/** px -> %, làm tròn 2 chữ số và kẹp 0–100 (ngược của pctToPx). */
export function pxToPct(px: PxBox, imgW: number, imgH: number, scale: number): Box {
  const conv = (v: number, total: number) => round2(clampPct((v / (total * scale)) * 100));
  return {
    x: conv(px.left, imgW),
    y: conv(px.top, imgH),
    w: conv(px.width, imgW),
    h: conv(px.height, imgH),
  };
}

/** Giữ khung nằm trọn trong ảnh: cạnh tối thiểu `min` (%), tràn phải/dưới thì đẩy ngược vào. */
export function clampBox(box: Box, min = 1): Box {
  const w = round2(Math.min(100, Math.max(min, box.w)));
  const h = round2(Math.min(100, Math.max(min, box.h)));
  const x = round2(Math.min(100 - w, Math.max(0, box.x)));
  const y = round2(Math.min(100 - h, Math.max(0, box.y)));
  return { x, y, w, h };
}

/** Khung QR vuông -> hộp chữ nhật %: h = size * imgW / imgH (vuông thật trên ảnh). */
export function qrToBox(qr: QrPos, imgW: number, imgH: number): Box {
  return { x: qr.x, y: qr.y, w: qr.size, h: round2((qr.size * imgW) / imgH) };
}

/** Hộp chữ nhật % -> khung QR: giữ chiều rộng làm `size`. */
export function boxToQr(box: Box): QrPos {
  return { x: box.x, y: box.y, size: box.w };
}

/**
 * Kết quả của một lần kéo chuột, tính thuần trên phần trăm.
 * `dxPct/dyPct` = độ dời so với lúc bấm chuột, tính theo % chiều rộng / chiều cao ảnh.
 * - `move`: dời khung, dừng ở mép ảnh (không đổi cỡ).
 * - `resize`: đổi cỡ từ góc phải dưới, giữ nguyên x/y; chạm mép phải/dưới thì dừng lớn thêm
 *   (không trượt khung). `square` (khung QR): giữ vuông thật trên ảnh, h = w * imgW / imgH.
 */
export function applyDrag(
  origin: Box,
  mode: 'move' | 'resize',
  dxPct: number,
  dyPct: number,
  imgW: number,
  imgH: number,
  square = false
): Box {
  const min = 1;
  if (mode === 'move') {
    return {
      x: round2(Math.min(100 - origin.w, Math.max(0, origin.x + dxPct))),
      y: round2(Math.min(100 - origin.h, Math.max(0, origin.y + dyPct))),
      w: origin.w,
      h: origin.h,
    };
  }
  if (square) {
    const room = Math.min(100 - origin.x, ((100 - origin.y) * imgH) / imgW);
    const size = round2(Math.max(min, Math.min(origin.w + dxPct, room)));
    return { x: origin.x, y: origin.y, w: size, h: round2((size * imgW) / imgH) };
  }
  return {
    x: origin.x,
    y: origin.y,
    w: round2(Math.max(min, Math.min(origin.w + dxPct, 100 - origin.x))),
    h: round2(Math.max(min, Math.min(origin.h + dyPct, 100 - origin.y))),
  };
}

/** Khung mã chữ (mã tay) dưới QR: hộp % + cỡ chữ (% chiều rộng ảnh). */
export interface CodeGeom extends Box {
  fontSize: number;
}

/** Chiều cao khung mã = 1.4 lần cỡ chữ (giống server), tính ra % chiều cao ảnh. */
const codeHeightPct = (fontSize: number, imgW: number, imgH: number) =>
  round2((fontSize * imgW * 1.4) / imgH);

/**
 * Khung mã mặc định ngay dưới QR, rộng bằng QR, chữ ~17% cạnh QR (tối thiểu 10px).
 * CHÉP công thức từ server `defaultShortBox` (hp-media-server-staging/src/gala/render/ticket-html.ts);
 * khác server: kẹp để khung không tràn đáy ảnh.
 */
export function defaultCodeBox(qr: QrPos, imgW: number, imgH: number): CodeGeom {
  const fontPx = Math.max(10, Math.round((qr.size / 100) * imgW * 0.17));
  const h = round2(((fontPx * 1.4) / imgH) * 100);
  const below = qr.y + (qr.size * imgW) / imgH;
  return {
    x: qr.x,
    y: round2(Math.min(100 - h, below)),
    w: qr.size,
    h,
    fontSize: round2((fontPx / imgW) * 100),
  };
}

/** Đổi cỡ chữ mã: chiều cao khung theo chữ, giữ nguyên tâm dọc (chữ không nhảy chỗ), kẹp trong ảnh. */
export function withCodeFontSize(
  geom: CodeGeom,
  fontSize: number,
  imgW: number,
  imgH: number
): CodeGeom {
  const h = Math.min(100, codeHeightPct(fontSize, imgW, imgH));
  const center = geom.y + geom.h / 2;
  return {
    ...geom,
    y: round2(Math.min(100 - h, Math.max(0, center - h / 2))),
    h,
    fontSize,
  };
}
